import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from 'sonner';
import { LogOut } from 'lucide-react';
import AdminProtectedRoute from './components/AdminProtectedRoute';
import { CRM_VERSION } from './version';
import './App.css';

// Admin Pages
import Dashboard from './pages/admin/Dashboard';
import Members from './pages/admin/Members';
import MemberDetail from './pages/admin/MemberDetail';
import Schedule from './pages/admin/Schedule';
import SessionDetail from './pages/admin/SessionDetail';
import BatchEntry from './pages/admin/BatchEntry';
import InbodyUpload from './pages/admin/InbodyUpload';
import ScoringRules from './pages/admin/ScoringRules';
import Products from './pages/admin/Products';
import Settings from './pages/admin/Settings';
import CheckIn from './pages/admin/CheckIn';
import FitReports from './pages/admin/FitReports';
import MemberInsights from './pages/admin/MemberInsights';
import FitReportCreate from './pages/admin/FitReportCreate';
import FitReportDetail from './pages/admin/FitReportDetail';
import FitReportPdfView from './pages/admin/FitReportPdfView';
import AdminLogin from './pages/admin/AdminLogin';

// Member App Pages
import MemberLogin from './pages/app/MemberLogin';
import MemberHome from './pages/app/MemberHome';
import MemberScore from './pages/app/MemberScore';
import MemberSettings from './pages/app/MemberSettings';
import MemberBook from './pages/app/MemberBook';
import MemberMyClasses from './pages/app/MemberMyClasses';
import MemberPasses from './pages/app/MemberPasses';
import MemberMeasurements from './pages/app/MemberMeasurements';

// Smart entry point for PWA
const MemberAppEntry = () => {
  const { isAuthenticated } = useAuth();
  return <Navigate to={isAuthenticated ? '/app/home' : '/app/login'} replace />;
};

// Root entry — 관리자 인증 여부에 따라 라우팅
const RootEntry = () => {
  const { isAdminAuthenticated } = useAuth();
  return <Navigate to={isAdminAuthenticated ? '/admin/dashboard' : '/admin/login'} replace />;
};

const AdminLogoutButton = () => {
  const { adminLogout, admin } = useAuth();
  const navigate = useNavigate();
  const handleLogout = () => {
    adminLogout();
    navigate('/admin/login', { replace: true });
  };
  return (
    <button
      type="button"
      onClick={handleLogout}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
      data-testid="admin-logout-btn"
      title={admin?.username ? `${admin.username} 로그아웃` : '로그아웃'}
    >
      <LogOut className="w-4 h-4" />
      로그아웃
    </button>
  );
};

// Admin Layout
const AdminLayout = ({ children }) => {
  return (
    <div className="min-h-screen bg-gray-100">
      <nav className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex justify-between h-16">
            <div className="flex space-x-8">
              <a href="/admin/dashboard" className="inline-flex items-center px-1 pt-1 text-sm font-medium text-gray-900">
                대시보드
              </a>
              <a href="/admin/members" className="inline-flex items-center px-1 pt-1 text-sm font-medium text-gray-500 hover:text-gray-900">
                회원관리
              </a>
              <a href="/admin/products" className="inline-flex items-center px-1 pt-1 text-sm font-medium text-gray-500 hover:text-gray-900">
                상품관리
              </a>
              <a href="/admin/schedule" className="inline-flex items-center px-1 pt-1 text-sm font-medium text-gray-500 hover:text-gray-900">
                스케줄
              </a>
              <a href="/admin/batch-entry" className="inline-flex items-center px-1 pt-1 text-sm font-medium text-gray-500 hover:text-gray-900">
                측정입력
              </a>
              <a href="/admin/inbody-upload" className="inline-flex items-center px-1 pt-1 text-sm font-medium text-gray-500 hover:text-gray-900" data-testid="nav-inbody-upload">
                인바디 업로드
              </a>
              <a href="/admin/scoring-rules" className="inline-flex items-center px-1 pt-1 text-sm font-medium text-gray-500 hover:text-gray-900">
                기준표
              </a>
              <a href="/admin/fit-reports" data-testid="nav-fit-reports" className="inline-flex items-center px-1 pt-1 text-sm font-medium text-gray-500 hover:text-gray-900">
                FIT REPORT
              </a>
              <a href="/admin/member-insights" data-testid="nav-member-insights" className="inline-flex items-center px-1 pt-1 text-sm font-medium text-gray-500 hover:text-gray-900">
                Member Insights
              </a>
              <a href="/admin/settings" className="inline-flex items-center px-1 pt-1 text-sm font-medium text-gray-500 hover:text-gray-900">
                설정
              </a>
            </div>
            <div className="flex items-center gap-3">
              <a
                href="/check-in"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="nav-checkin"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
              >
                출석 체크
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor"><path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" /><path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z" /></svg>
              </a>
              <span className="text-xs text-gray-400" data-testid="crm-version">v{CRM_VERSION}</span>
              <AdminLogoutButton />
            </div>
          </div>
        </div>
      </nav>
      <main className="max-w-7xl mx-auto">{children}</main>
    </div>
  );
};

// Admin route wrapper — layout + protection 한 번에 적용
const AdminRoute = ({ children }) => (
  <AdminProtectedRoute>
    <AdminLayout>{children}</AdminLayout>
  </AdminProtectedRoute>
);

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster 
          position="top-center" 
          toastOptions={{
            style: {
              background: '#1A1D23',
              color: '#FFFFFF',
              border: '1px solid #2A2E38',
            },
            className: 'dark-toast',
          }}
        />
        <Routes>
          {/* Root redirect */}
          <Route path="/" element={<RootEntry />} />

          {/* Admin Login (unauthenticated route) */}
          <Route path="/admin/login" element={<AdminLogin />} />

          {/* Admin Routes (protected) */}
          <Route path="/admin/dashboard" element={<AdminRoute><Dashboard /></AdminRoute>} />
          <Route path="/admin/members" element={<AdminRoute><Members /></AdminRoute>} />
          <Route path="/admin/members/:memberId" element={<AdminRoute><MemberDetail /></AdminRoute>} />
          <Route path="/admin/products" element={<AdminRoute><Products /></AdminRoute>} />
          <Route path="/admin/schedule" element={<AdminRoute><Schedule /></AdminRoute>} />
          <Route path="/admin/sessions/:sessionId" element={<AdminRoute><SessionDetail /></AdminRoute>} />
          <Route path="/admin/batch-entry" element={<AdminRoute><BatchEntry /></AdminRoute>} />
          <Route path="/admin/inbody-upload" element={<AdminRoute><InbodyUpload /></AdminRoute>} />
          <Route path="/admin/scoring-rules" element={<AdminRoute><ScoringRules /></AdminRoute>} />
          <Route path="/admin/fit-reports" element={<AdminRoute><FitReports /></AdminRoute>} />
          <Route path="/admin/fit-reports/new" element={<AdminRoute><FitReportCreate /></AdminRoute>} />
          <Route path="/admin/fit-reports/:reportId" element={<AdminRoute><FitReportDetail /></AdminRoute>} />
          {/* PDF view는 Playwright 렌더링용 별도 라우트. Playwright는 로컬(3000)에서 접근하며 관리자 인증 미들웨어를
              통과할 수 없으므로 프로텍션 제외한다. 대신 서버-사이드에서만 호출되며 외부 브라우저로 열더라도
              특정 report_id 미소지 시 렌더링되지 않는 read-only 페이지이다. */}
          <Route path="/admin/fit-reports/:reportId/pdf-view" element={<FitReportPdfView />} />
          <Route path="/admin/member-insights" element={<AdminRoute><MemberInsights /></AdminRoute>} />
          <Route path="/admin/settings" element={<AdminRoute><Settings /></AdminRoute>} />
          
          {/* Tablet Check-In (full screen, no menu) */}
          <Route path="/check-in" element={<CheckIn />} />
          
          {/* Member App Routes */}
          <Route path="/app" element={<MemberAppEntry />} />
          <Route path="/app/login" element={<MemberLogin />} />
          <Route path="/app/home" element={<MemberHome />} />
          <Route path="/app/score" element={<MemberScore />} />
          <Route path="/app/settings" element={<MemberSettings />} />
          <Route path="/app/book" element={<MemberBook />} />
          <Route path="/app/my-classes" element={<MemberMyClasses />} />
          <Route path="/app/passes" element={<MemberPasses />} />
          <Route path="/app/measurements" element={<MemberMeasurements />} />
          
          {/* Fallback */}
          <Route path="*" element={<RootEntry />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
